import { createContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LabelValue } from "types";
import useExercises from "../useExercises";
import useNaturalSort from "hooks/useNaturalSort";

export interface BodySegmentsType {
  bodySegmentsList: LabelValue[];
}

export const BodySegmentsContext = createContext<LabelValue[] | undefined>(undefined);
BodySegmentsContext.displayName = "BodySegmentsContext";

export interface BodySegmentsActionsType {
  getBodySegmentsValuesFromList: (segments: LabelValue[]) => string[];
  filterBodySegments: (segments: string[]) => LabelValue[];
}

export const BodySegmentsActionsContext = createContext<BodySegmentsActionsType | undefined>(undefined);
BodySegmentsActionsContext.displayName = "BodySegmentsActionsContext";

interface BodySegmentsProviderProps {
  children?: React.ReactNode;
}

function BodySegmentsProvider(props: BodySegmentsProviderProps) {
  const { t } = useTranslation();
  const exercises = useExercises();
  const naturalCompare = useNaturalSort();

  const allBodySegments = useMemo(
    () => Array.from(new Set(exercises.map((exercise) => exercise.bodySegments).flat())).sort(naturalCompare),
    [exercises, naturalCompare]
  );

  /** List of all body segments with translated label and value */
  const bodySegmentsList = useMemo(
    () =>
      allBodySegments.map((bs) => ({ label: t("exercise_body_part", { context: bs, defaultValue: bs }), value: bs })),
    [allBodySegments, t]
  );

  /** Parse body segments list to values */
  const getBodySegmentsValuesFromList = useCallback((segments: Array<LabelValue>) => {
    return segments.map((segment) => segment.value);
  }, []);

  /** Filter body segments list by segments, used to show the translated values */
  const filterBodySegments = useCallback(
    (segments: string[]) => {
      return bodySegmentsList.filter((segment) => segments.includes(segment.value));
    },
    [bodySegmentsList]
  );

  const actions = useMemo(
    () => ({
      getBodySegmentsValuesFromList,
      filterBodySegments,
    }),
    [getBodySegmentsValuesFromList, filterBodySegments]
  );

  return (
    <BodySegmentsActionsContext.Provider value={actions}>
      <BodySegmentsContext.Provider value={bodySegmentsList}>{props.children}</BodySegmentsContext.Provider>
    </BodySegmentsActionsContext.Provider>
  );
}

export default BodySegmentsProvider;
